import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircleHelp } from "lucide-react";

export interface HelpTooltipProps {
  label: string;
  content: string;
  align?: "left" | "center" | "right";
}

const TOOLTIP_GAP = 8;
const VIEWPORT_MARGIN = 8;
const DEFAULT_TOOLTIP_WIDTH = 288;

interface TooltipPosition {
  left: number;
  top: number;
}

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

const getTooltipPosition = ({
  align,
  tooltip,
  trigger,
}: {
  align: NonNullable<HelpTooltipProps["align"]>;
  tooltip: HTMLElement;
  trigger: HTMLElement;
}): TooltipPosition => {
  const triggerBounds = trigger.getBoundingClientRect();
  const tooltipWidth = tooltip.offsetWidth || DEFAULT_TOOLTIP_WIDTH;
  const tooltipHeight = tooltip.offsetHeight;
  const spaceAbove = triggerBounds.top - VIEWPORT_MARGIN;
  const spaceBelow =
    window.innerHeight - triggerBounds.bottom - VIEWPORT_MARGIN;
  const opensAbove =
    spaceBelow < tooltipHeight + TOOLTIP_GAP && spaceAbove > spaceBelow;
  const top = opensAbove
    ? triggerBounds.top - tooltipHeight - TOOLTIP_GAP
    : triggerBounds.bottom + TOOLTIP_GAP;
  const unclampedLeft = {
    left: triggerBounds.left,
    center:
      triggerBounds.left + triggerBounds.width / 2 - tooltipWidth / 2,
    right: triggerBounds.right - tooltipWidth,
  }[align];

  return {
    left: clamp(
      unclampedLeft,
      VIEWPORT_MARGIN,
      window.innerWidth - tooltipWidth - VIEWPORT_MARGIN
    ),
    top: clamp(
      top,
      VIEWPORT_MARGIN,
      window.innerHeight - tooltipHeight - VIEWPORT_MARGIN
    ),
  };
};

export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  label,
  content,
  align = "left",
}) => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const isOpen = isHovered || isFocused;

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !tooltipRef.current) return;
    const updatePosition = () => {
      if (!triggerRef.current || !tooltipRef.current) return;
      setPosition(
        getTooltipPosition({
          align,
          tooltip: tooltipRef.current,
          trigger: triggerRef.current,
        })
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [align, content, isOpen]);

  return (
    <>
      <span
        className="relative inline-flex shrink-0"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <button
          ref={triggerRef}
          type="button"
          aria-label={label}
          aria-describedby={isOpen ? tooltipId : undefined}
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            setIsFocused(false);
            event.currentTarget.blur();
          }}
        >
          <CircleHelp aria-hidden="true" className="h-4 w-4" />
        </button>
      </span>
      {isOpen &&
        createPortal(
        <span
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className="fixed z-[100] w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-gray-900 px-3 py-2 text-left text-sm font-normal leading-5 normal-case tracking-normal text-white shadow-lg"
          style={{
            left: position?.left ?? 0,
            top: position?.top ?? 0,
            visibility: position ? "visible" : "hidden",
          }}
        >
          {content}
        </span>,
        document.body
      )}
    </>
  );
};
